import { useMemo, useCallback } from 'react';
import {
  useSelectLabelOptionsLazyQuery,
  WbsLabelValueSearchResult,
  WbsProjectLabelSearchQueryResult,
  SelectLabelOptionsQuery,
  SelectLabelOptionsQueryVariables,
  SelectLabelOptionsDocument
} from 'graphql/types';
import { FacetOption } from 'modules/notes/common';

export const OEF_PAGE_COUNT = 50;

const getOefOptions = (
  data: SelectLabelOptionsQuery | undefined
): WbsLabelValueSearchResult[] =>
  data?.label?.selectOptions?.items
    .filter(({ id }) => id)
    .map(({ id, name }) => ({ id, displayText: name })) ?? [];

export interface OefSearchParams {
  id: string;
  query?: string;
}

export const useOefLabelSearch = (): {
  options: WbsLabelValueSearchResult[];
  loading: boolean;
  searchHandler: (params: OefSearchParams) => void;
  hasMore: boolean;
} => {
  const [getLabelOptions, { data, loading }] = useSelectLabelOptionsLazyQuery();

  const searchHandler = useCallback(
    ({ id, query }: OefSearchParams) => {
      const variables: SelectLabelOptionsQueryVariables = {
        id,
        input: {
          query,
          count: OEF_PAGE_COUNT
        }
      };

      getLabelOptions({ variables });
    },
    [getLabelOptions]
  );

  const oefs = useMemo(() => getOefOptions(data), [data]);

  return useMemo(
    () => ({
      searchHandler,
      options: oefs,
      loading,
      hasMore: oefs.length === OEF_PAGE_COUNT
    }),
    [loading, searchHandler, oefs]
  );
};

export const searchOefs = ({ client: apolloClient }: { client: any }) => async (
  variables: SelectLabelOptionsQueryVariables
): Promise<WbsLabelValueSearchResult[]> => {
  const { data }: WbsProjectLabelSearchQueryResult = await apolloClient.query({
    query: SelectLabelOptionsDocument,
    variables: variables
  });

  return getOefOptions(data);
};

const transformOef = (
  data: SelectLabelOptionsQuery | undefined
): FacetOption[] =>
  data?.label?.selectOptions?.items.map((oef) => ({
    ...oef,
    label: oef.name,
    key: oef.name,
    id: oef.id || undefined
  })) ?? [];

export const searchOefsByTerm = ({
  client: apolloClient,
  labelId
}: {
  client: any;
  labelId: string;
}) => async (searchTerm: string): Promise<FacetOption[]> => {
  const { data }: WbsProjectLabelSearchQueryResult = await apolloClient.query({
    query: SelectLabelOptionsDocument,
    variables: { id: labelId, input: { count: 10, query: searchTerm } }
  });

  return transformOef(data);
};

export default useOefLabelSearch;
